import {
  useMemo,
  useRef,
  useEffect,
  Children,
  isValidElement,
  createContext,
  useContext
} from "react";
import {
  NavLink as NavLinkV6,
  useNavigate,
  useLocation,
  useParams as useParamsV6,
  matchPath,
  useNavigationType,
  useResolvedPath
} from "react-router-dom-original";

// ─────────────────────────────────────────────────────────
// V5 Context
// ─────────────────────────────────────────────────────────
const V5RouterContext = createContext({
  params: {},
  url: "",
  path: ""
});

// ─────────────────────────────────────────────────────────
// Custom useParams
// ─────────────────────────────────────────────────────────
export function useParams() {
  const v6Params = useParamsV6();
  const v5Context = useContext(V5RouterContext);
  return { ...v6Params, ...v5Context.params };
}

// ─────────────────────────────────────────────────────────
// useBaseMatch — THE FIX FOR NESTED DYNAMIC ROUTES
// ─────────────────────────────────────────────────────────
function useBaseMatch() {
  const resolved = useResolvedPath("");
  const location = useLocation();
  const v6Params = useParamsV6();
  const context = useContext(V5RouterContext);

  return useMemo(() => {
    // If we are deep inside a v5 Switch, use the matched context
    if (context.path) {
      return {
        path: context.path,
        url: context.url,
        isExact: location.pathname === context.url,
        params: { ...v6Params, ...context.params }
      };
    }

    // If we are at the root of the MFE, use v6's resolved path from the Container
    let basePath = resolved.pathname;
    if (basePath !== "/" && basePath.endsWith("/")) {
      basePath = basePath.slice(0, -1); // Clean trailing slashes
    }

    return {
      path: basePath,
      url: basePath,
      isExact: location.pathname === basePath,
      params: v6Params
    };
  }, [context, resolved.pathname, location.pathname, v6Params]);
}

// ─────────────────────────────────────────────────────────
// useHistory
// ─────────────────────────────────────────────────────────
export function useHistory() {
  const navigate = useNavigate();
  const location = useLocation();
  const action = useNavigationType();
  const historyRef = useRef();

  if (!historyRef.current) {
    historyRef.current = {
      push: (path, state) => navigate(path, { state }),
      replace: (path, state) => navigate(path, { replace: true, state }),
      goBack: () => navigate(-1),
      goForward: () => navigate(1),
      go: (delta) => navigate(delta),
      block: () => {
        console.warn("history.block() not supported.");
        return () => {};
      },
      listen: () => {
        console.warn("history.listen() not supported.");
        return () => {};
      },
      createHref: (to) => {
        if (typeof to === "string") return to;
        return `${to.pathname || ""}${to.search || ""}${to.hash || ""}`;
      }
    };
  }

  historyRef.current.location = location;
  historyRef.current.action = action;
  historyRef.current.length = window.history.length;

  return historyRef.current;
}

// ─────────────────────────────────────────────────────────
// useRouteMatch
// ─────────────────────────────────────────────────────────
export function useRouteMatch(pathPattern) {
  const location = useLocation();
  const baseMatch = useBaseMatch();

  return useMemo(() => {
    if (!pathPattern) return baseMatch;

    // console.log(`\n--- [useRouteMatch DEBUG] Hook called with:`, pathPattern);

    const patterns = Array.isArray(pathPattern) ? pathPattern : [pathPattern];
    const cleanPathname =
      location.pathname.endsWith("/") && location.pathname !== "/"
        ? location.pathname.slice(0, -1)
        : location.pathname;

    for (let patternObj of patterns) {
      const rawPattern =
        typeof patternObj === "string" ? patternObj : patternObj.path;
      const exact =
        typeof patternObj === "object" ? patternObj.exact === true : false;

      const { v6Path, constraints } = compileV5Path(rawPattern);
      const cleanPattern =
        v6Path.endsWith("/") && v6Path !== "/" ? v6Path.slice(0, -1) : v6Path;
      const finalPattern = exact ? cleanPattern : `${cleanPattern}/*`;

      const match = matchPath(
        { path: finalPattern, end: exact },
        cleanPathname
      );

      if (match && validateConstraints(match.params, constraints)) {
        // console.log(`  ✅ Hook Matched:`, rawPattern);
        return {
          path: rawPattern,
          url: match.pathnameBase || match.pathname,
          isExact: match.pathname === cleanPathname,
          params: { ...baseMatch.params, ...match.params }
        };
      }
    }

    // console.log(`  ❌ Hook Failed to match.`);
    return null;
  }, [location.pathname, pathPattern, baseMatch]);
}

// ─────────────────────────────────────────────────────────
// V5RoutePropsWrapper
// ─────────────────────────────────────────────────────────
const V5RoutePropsWrapper = ({
  component: Component,
  render,
  computedMatch
}) => {
  const history = useHistory();
  const location = useLocation();

  const routeProps = { match: computedMatch, location, history };

  if (Component) return <Component {...routeProps} />;
  if (render) return <>{render(routeProps)}</>;
  return null;
};

// ─────────────────────────────────────────────────────────
// Redirect
// ─────────────────────────────────────────────────────────
export const Redirect = ({ to, push }) => {
  const navigate = useNavigate();

  const destination = useMemo(() => {
    if (typeof to === "string") return to;
    return `${to?.pathname || ""}${to?.search || ""}${to?.hash || ""}`;
  }, [to]);

  const state = typeof to === "object" ? to.state : undefined;

  useEffect(() => {
    navigate(destination, { replace: !push, state });
  }, [navigate, destination, push]);

  return null;
};
Redirect.displayName = "Redirect";

// ─────────────────────────────────────────────────────────
// Switch
// ─────────────────────────────────────────────────────────
export const Switch = ({ children }) => {
  const location = useLocation();
  const baseMatch = useBaseMatch();

  let match = null;
  let matchedChild = null;
  let matchedPath = null;

  const cleanPathname =
    location.pathname.endsWith("/") && location.pathname !== "/"
      ? location.pathname.slice(0, -1)
      : location.pathname;

  // console.log(`\n--- [SWITCH DEBUG] Evaluating URL: ${cleanPathname} ---`);

  Children.forEach(children, (child) => {
    if (match == null && isValidElement(child)) {
      const path = child.props.path || child.props.from;
      const exact = child.props.exact === true;

      if (path) {
        const paths = Array.isArray(path) ? path : [path];
        for (const p of paths) {
          const { v6Path, constraints } = compileV5Path(p);

          let clean =
            v6Path.endsWith("/") && v6Path !== "/"
              ? v6Path.slice(0, -1)
              : v6Path;
          if (clean === "") clean = "/";
          const pattern = exact ? clean : `${clean}/*`;

          const m = matchPath({ path: pattern, end: exact }, cleanPathname);

          // DEBUG LOGGING
          // console.log(`Checking Route: "${p}"`);
          // console.log(`  -> Converted to v6 Pattern: "${pattern}"`);
          // console.log(`  -> Did v6 match?`, m ? "YES" : "NO");

          if (m) {
            const regexPassed = validateConstraints(m.params, constraints);
            // console.log(
            //   `  -> Did Regex constraints pass?`,
            //   regexPassed ? "YES" : "NO"
            // );

            if (regexPassed) {
              match = m;
              matchedChild = child;
              matchedPath = p;
              // console.log(`  ✅ MATCH SUCCESS! Rendering this route.`);
              break;
            }
          }
        }
      } else {
        // Catch-all route (404)
        // console.log(`Checking Route: Catch-All (No path provided)`);
        match = { params: {}, pathname: cleanPathname };
        matchedChild = child;
        matchedPath = baseMatch.path;
        // console.log(`  ⚠️ Fell through to Catch-All (404)`);
      }
    }
  });

  if (!match || !matchedChild) {
    // console.log(`❌ Switch found NO matches and NO catch-all. Rendering null.`);
    return null;
  }

  const {
    component,
    render,
    children: routeChildren,
    element
  } = matchedChild.props;
  const displayName = matchedChild.type?.displayName || matchedChild.type?.name;

  if (displayName === "Redirect" || matchedChild.type === Redirect) {
    return (
      <Redirect to={matchedChild.props.to} push={matchedChild.props.push} />
    );
  }

  const mergedParams = { ...baseMatch.params, ...match.params };
  const computedMatch = {
    params: mergedParams,
    path: matchedPath,
    url: match.pathnameBase || match.pathname,
    isExact: match.pathname === cleanPathname
  };

  const contextValue = {
    params: mergedParams,
    url: computedMatch.url,
    path: matchedPath
  };

  let renderedElement;
  if (element) renderedElement = element;
  else if (component)
    renderedElement = (
      <V5RoutePropsWrapper
        component={component}
        computedMatch={computedMatch}
      />
    );
  else if (render)
    renderedElement = (
      <V5RoutePropsWrapper render={render} computedMatch={computedMatch} />
    );
  else if (typeof routeChildren === "function")
    renderedElement = (
      <>
        {routeChildren({
          match: computedMatch,
          location,
          history: useHistory()
        })}
      </>
    );
  else renderedElement = <>{routeChildren}</>;

  return (
    <V5RouterContext.Provider value={contextValue}>
      {renderedElement}
    </V5RouterContext.Provider>
  );
};

// ─────────────────────────────────────────────────────────
// CompatNavLink
// ─────────────────────────────────────────────────────────
export const CompatNavLink = ({
  to,
  children,
  end,
  exact,
  activeClassName,
  activeStyle,
  className,
  style,
  ...rest
}) => {
  const isV5Usage =
    activeClassName !== undefined ||
    activeStyle !== undefined ||
    exact !== undefined;
  const shouldEnd = end ?? exact ?? false;

  if (isV5Usage) {
    return (
      <NavLinkV6
        to={to}
        end={shouldEnd}
        className={({ isActive, isPending }) => {
          const base =
            typeof className === "function"
              ? className({ isActive, isPending })
              : className || "";
          const active = isActive ? activeClassName || "" : "";
          return `${base} ${active}`.trim();
        }}
        style={({ isActive, isPending }) => {
          const baseStyle =
            typeof style === "function"
              ? style({ isActive, isPending })
              : style || {};
          return { ...baseStyle, ...(isActive ? activeStyle || {} : {}) };
        }}
        {...rest}
      >
        {children}
      </NavLinkV6>
    );
  }

  return (
    <NavLinkV6
      to={to}
      end={shouldEnd}
      className={className}
      style={style}
      {...rest}
    >
      {children}
    </NavLinkV6>
  );
};
CompatNavLink.displayName = "NavLink";

// ─────────────────────────────────────────────────────────
// withRouter
// ─────────────────────────────────────────────────────────
export function withRouter(Component) {
  const Wrapped = (props) => {
    const history = useHistory();
    const location = useLocation();
    const match = useBaseMatch();

    return (
      <Component
        {...props}
        history={history}
        location={location}
        match={match}
      />
    );
  };

  Wrapped.displayName = `withRouter(${Component.displayName || Component.name || "Component"})`;
  return Wrapped;
}

// ─────────────────────────────────────────────────────────
// REGEX PATH COMPILERS
// ─────────────────────────────────────────────────────────
function compileV5Path(path) {
  if (!path || typeof path !== "string")
    return { v6Path: path, constraints: {} };

  const constraints = {};
  const v6Path = path.replace(
    /:(\w+)\(([^)]+)\)/g,
    (_, paramName, regexPattern) => {
      // Added 'i' flag for case-insensitivity just in case the URL casing is slightly off
      constraints[paramName] = new RegExp(`^${regexPattern}$`, "i");
      return `:${paramName}`;
    }
  );

  return { v6Path, constraints };
}

function validateConstraints(params, constraints) {
  for (const key in constraints) {
    if (params[key] && !constraints[key].test(params[key])) {
      return false;
    }
  }
  return true;
}
